import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAppDispatch, useAppSelector } from '../../store/hooks';
import {
  loginUser,
  clearRedirectUrl,
  getUserProfile,
  clearPasswordChangeFlag,
  setSuspiciousActivity,
  setPostAuthRedirect,
  setCredentials,
} from '../../store/slices/authSlice';
import { setLoading } from '../../store/slices/uiSlice';
import { Eye, EyeOff } from 'lucide-react';
import toast from 'react-hot-toast';
import TextCaptcha from './TextCaptcha';
import AccountInactiveModal from './AccountInactiveModal';
import ForcePasswordChangeModal from './ForcePasswordChangeModal';
import RememberedAccountsList from './RememberedAccountsList';
import {
  getRememberedAccounts,
  removeRememberedAccount,
  restoreRememberMeAccountToSession,
} from '../../utils/rememberMe';
import useEmailFieldValidation from '../../hooks/useEmailFieldValidation';
import { getEmailValidationError, getEmailValidationInputClass } from '../../utils/emailValidation';
import EmailFieldFeedback from '../EmailInput/EmailFieldFeedback';

const getPostLoginTargetUrl = (accountUser) => {
  const storedRedirectUrl = sessionStorage.getItem('redirectAfterLogin');
  if (accountUser.user_type === 'Admin' || accountUser.user_type === 'SubAdmin') {
    return '/admin';
  }
  if (accountUser.user_type === 'Airliner') {
    return '/airliner/profile';
  }
  if (accountUser.user_type === 'JetSetter') {
    return storedRedirectUrl || '/jetsetter/profile/user-details';
  }
  return storedRedirectUrl || '/jetsetter/profile/user-details';
};

const LoginForm = ({ onSignUpClick, onForgotPasswordClick, onClose }) => {
  const isSuspiciousRole = (accountUser) =>
    accountUser?.user_type === 'JetSetter' || accountUser?.user_type === 'Airliner';

  const [formData, setFormData] = useState({
    email: '',
    password: '',
    rememberMe: false,
  });
  const [rememberedAccounts, setRememberedAccounts] = useState(() => getRememberedAccounts());
  const [continuingUserId, setContinuingUserId] = useState(null);

  const [errors, setErrors] = useState({});
  const {
    emailTouched,
    emailError,
    onEmailChange,
    onEmailBlur,
    validateEmailForSubmit,
  } = useEmailFieldValidation({ requiredMessage: 'Please enter a User ID' });
  const [showInactiveModal, setShowInactiveModal] = useState(false);
  const [captchaToken, setCaptchaToken] = useState(null);
  const [showForcePasswordChange, setShowForcePasswordChange] = useState(false);
  const [captchaKey, setCaptchaKey] = useState(Date.now());
  const [pendingSuspiciousLogin, setPendingSuspiciousLogin] = useState(false);
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const { loading } = useAppSelector((state) => state.ui);
  const {
    redirectUrl,
    user,
    suspiciousActivity,
    twofa_type,
    twofa_questions,
    twofa_email,
    twofa_password,
    twofa_phone,
    requiresPasswordChange,
  } = useAppSelector((state) => state.auth);
  const [showPassword, setShowPassword] = useState(false);

  // Check if password change is required after login
  useEffect(() => {
    if (requiresPasswordChange && user) {
      console.log('🔒 Password change required - showing modal');
      setShowForcePasswordChange(true);
    }
  }, [requiresPasswordChange, user]);

  // Handle redirection after successful login
  useEffect(() => {
    // Check if password change is required first
    if (requiresPasswordChange) {
      return; // Don't redirect, show password change modal
    }

    // Check if 2FA is required first
    if (twofa_type && twofa_email) {
      console.log('🔐 2FA required - redirecting to 2FA verification page');
      // Store 2FA data in sessionStorage for TwoFactorVerifyPage
      sessionStorage.setItem('twofa_email', twofa_email);
      if (twofa_questions && twofa_questions.length > 0) {
        sessionStorage.setItem('twofa_questions', JSON.stringify(twofa_questions));
      }
      if (twofa_phone) {
        sessionStorage.setItem('twofa_phone', twofa_phone);
      }
      
      const method = twofa_type === 'security_que' ? 'security_questions' : twofa_type;
      navigate(`/2fa-verify?method=${method}`);
      onClose();
      return;
    }

    if (redirectUrl && user) {
      console.log('🔄 LoginForm: Handling login redirection:', {
        userType: user.user_type,
        redirectUrl: redirectUrl,
        isAuthenticated: true,
        tokenInLocalStorage: sessionStorage.getItem('token') ? 'YES' : 'NO'
      });

      // Clean up ALL preserved booking states from sessionStorage when logging in
      // This ensures no leftover state from previous users
      const currentUserId = user.id || 'guest';
      const keysToRemove = [];
      
      console.log('🔍 LoginForm: Checking for preserved booking states, current user ID:', currentUserId);
      
      for (let i = 0; i < sessionStorage.length; i++) {
        const key = sessionStorage.key(i);
        if (key && key.startsWith('preserved_booking_state_')) {
          console.log('🔍 Found preserved booking state key:', key);
          
          // Try to parse and check userId
          try {
            const stateData = sessionStorage.getItem(key);
            if (stateData) {
              const parsed = JSON.parse(stateData);
              const stateUserId = parsed.userId?.toString();
              const currentUserIdStr = currentUserId?.toString();
              
              console.log('🔍 State userId:', stateUserId, 'Current userId:', currentUserIdStr);
              
              if (stateUserId !== currentUserIdStr) {
                console.log('❌ User ID mismatch - will remove this key');
                keysToRemove.push(key);
              } else {
                console.log('✅ User ID matches - keeping this key');
              }
            }
          } catch (error) {
            console.error('Failed to parse preserved state, will remove:', error);
            keysToRemove.push(key);
          }
        }
      }
      
      // Remove preserved states from other users
      keysToRemove.forEach(key => {
        sessionStorage.removeItem(key);
        console.log('🧹 Cleaned up preserved booking state:', key);
      });
      
      console.log(`✅ Cleanup complete. Removed ${keysToRemove.length} preserved states from other users`);

      // Check if there's preserved booking state (from session expiration) for THIS user
      const preservedBookingState = sessionStorage.getItem(`preserved_booking_state_${currentUserId}`);
      let shouldUsePreservedState = false;
      
      console.log('🔍 Checking for current user preserved state:', `preserved_booking_state_${currentUserId}`);
      console.log('🔍 Found:', preservedBookingState ? 'YES' : 'NO');
      
      if (preservedBookingState) {
        try {
          const bookingState = JSON.parse(preservedBookingState);
          console.log('📦 Parsed booking state:', bookingState);
          
          // Verify the userId matches - CRITICAL for security
          const stateUserId = bookingState.userId?.toString();
          const currentUserIdStr = currentUserId?.toString();
          
          console.log('🔍 Verifying userId - State:', stateUserId, 'Current:', currentUserIdStr);
          
          if (bookingState.userId && stateUserId === currentUserIdStr && bookingState.returnPath) {
            console.log('✅ User ID matches AND has returnPath - will redirect to booking flow');
            shouldUsePreservedState = true;
            dispatch(setPostAuthRedirect(bookingState.returnPath));

            if (suspiciousActivity?.id || pendingSuspiciousLogin) {
              onClose();
              return;
            }
            
            // Close the login modal
            onClose();
            // Clear the redirect URL
            dispatch(clearRedirectUrl());
            
            // Navigate to the preserved path
            const returnUrl = bookingState.returnPath;
            console.log('🔄 Redirecting to preserved booking flow:', returnUrl);
            
            setTimeout(() => {
              navigate(returnUrl, { replace: true });
              // Clear the preserved state after using it
              sessionStorage.removeItem(`preserved_booking_state_${currentUserId}`);
              console.log('🧹 Cleared preserved state after redirect');
            }, 200);
            
            return;
          } else {
            // State doesn't match current user or missing data, clear it
            console.log('❌ Preserved booking state INVALID - userId mismatch or missing returnPath');
            console.log('State userId:', stateUserId, 'Current userId:', currentUserIdStr, 'Has returnPath:', !!bookingState.returnPath);
            sessionStorage.removeItem(`preserved_booking_state_${currentUserId}`);
            console.log('🧹 Cleared invalid preserved state');
          }
        } catch (error) {
          console.error('❌ Failed to parse preserved booking state:', error);
          sessionStorage.removeItem(`preserved_booking_state_${currentUserId}`);
        }
      }
      
      console.log('ℹ️ No valid preserved state for current user - continuing with normal redirect');
      
      // If we reach here, there's no valid preserved state for this user
      // Continue with normal redirect logic
      
      // Check if there's a stored redirect URL from protected route or flight selection
      let storedRedirectUrl = sessionStorage.getItem('redirectAfterLogin');
      
      // CRITICAL: If storedRedirectUrl is a booking flow URL but there's no preserved state,
      // it means it's from a different user's session - IGNORE IT
      if (storedRedirectUrl && storedRedirectUrl.includes('/flight-booking-flow')) {
        // Check if there's a valid preserved state for this user
        const hasValidPreservedState = sessionStorage.getItem(`preserved_booking_state_${currentUserId}`);
        if (!hasValidPreservedState) {
          console.log('⚠️ Found booking flow URL in redirectAfterLogin but no preserved state - ignoring it');
          storedRedirectUrl = null;
          sessionStorage.removeItem('redirectAfterLogin');
        }
      }

      // All redirections are now internal - determine target URL based on user type
      let targetUrl;
      
      if (user.user_type === 'Admin' || user.user_type === 'SubAdmin') {
        // Admin users go to admin dashboard
        targetUrl = '/admin';
      } else if (user.user_type === 'Airliner') {
        // Airliner always goes to their profile page
        targetUrl = '/airliner/profile';
      } else if (user.user_type === 'JetSetter') {
        // JetSetter stays on the same page if there's a stored URL, otherwise goes to profile
        targetUrl = storedRedirectUrl || '/jetsetter/profile/user-details';
      } else {
        // Fallback for other user types
        targetUrl = redirectUrl || storedRedirectUrl || '/jetsetter/profile/user-details';
      }
      // Clear stored redirect URL
      sessionStorage.removeItem('redirectAfterLogin');
      dispatch(setPostAuthRedirect(targetUrl));

      if (suspiciousActivity?.id || pendingSuspiciousLogin) {
        onClose();
        return;
      }

      console.log('⏱️ LoginForm: Waiting 200ms before navigation to ensure token is set...');
      console.log('🎯 Target URL determined:', targetUrl);
      
      // Increased delay to ensure Redux state and axios defaults are fully updated
      setTimeout(() => {
        console.log('🚀 LoginForm: Navigating to:', targetUrl);
        navigate(targetUrl);
        onClose();
      }, 200);

      // Clear the redirect URL after handling
      dispatch(clearRedirectUrl());
    }
  }, [redirectUrl, user, suspiciousActivity, pendingSuspiciousLogin, navigate, onClose, dispatch]);

  // Redirect to 2FA verification page when 2FA is required
  useEffect(() => {
    if (twofa_type === 'security_que' && twofa_questions && twofa_questions.length > 0) {
      console.log('🔐 2FA Required - Redirecting to security question verification page');
      console.log('📋 Questions:', twofa_questions);
      console.log('📧 Email:', twofa_email);
      
      // Store 2FA data in sessionStorage for the verification page
      sessionStorage.setItem('twofa_email', twofa_email);
      sessionStorage.setItem('twofa_password', twofa_password);
      sessionStorage.setItem('twofa_questions', JSON.stringify(twofa_questions));
      
      // Close the login modal first
      onClose();
      
      // Then redirect to 2FA security question verification page
      setTimeout(() => {
        navigate('/2fa-verify');
      }, 100);
    }
  }, [twofa_type, twofa_questions, twofa_email, twofa_password, navigate, onClose]);

  // Redirect to 2FA verification page when OTP 2FA is required
  useEffect(() => {
    if ((twofa_type === 'email' || twofa_type === 'sms') && twofa_email) {
      console.log('🔐 2FA Required - Redirecting to OTP verification page');
      console.log('📧 Email:', twofa_email);
      console.log('📱 Phone:', twofa_phone);
      
      // Store 2FA data in sessionStorage for the verification page
      sessionStorage.setItem('twofa_email', twofa_email);
      sessionStorage.setItem('twofa_password', twofa_password);
      sessionStorage.setItem('twofa_phone', twofa_phone || '');
      
      // Close the login modal first
      onClose();
      
      // Then redirect to 2FA OTP verification page
      setTimeout(() => {
        navigate(`/2fa-verify?method=${twofa_type}`);
      }, 100);
    }
  }, [twofa_type, twofa_email, twofa_password, twofa_phone, navigate, onClose]);

  const refreshRememberedAccounts = () => {
    setRememberedAccounts(getRememberedAccounts());
  };

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    const nextValue = type === 'checkbox' ? checked : value;

    if (name === 'email' && type !== 'checkbox') {
      onEmailChange(nextValue);
      const match = getRememberedAccounts().find(
        (a) => a.user?.email?.toLowerCase() === String(nextValue).trim().toLowerCase()
      );
      setFormData({
        ...formData,
        email: nextValue,
        rememberMe: match ? true : formData.rememberMe,
      });
      return;
    }

    setFormData({
      ...formData,
      [name]: nextValue,
    });
  };

  const handleContinueAsAccount = async (userId) => {
    setContinuingUserId(userId);
    try {
      const credentials = restoreRememberMeAccountToSession(userId);
      if (!credentials) {
        toast.error('Saved session expired. Please sign in with your password.');
        removeRememberedAccount(userId);
        refreshRememberedAccounts();
        return;
      }

      dispatch(setCredentials(credentials));

      const profile = await dispatch(getUserProfile()).unwrap();
      toast.success(`Welcome back, ${profile.first_name}!`);

      const targetUrl = getPostLoginTargetUrl(profile);
      sessionStorage.removeItem('redirectAfterLogin');
      dispatch(setPostAuthRedirect(targetUrl));

      setTimeout(() => {
        navigate(targetUrl);
        onClose();
      }, 200);
    } catch {
      toast.error('Saved session expired. Please sign in with your password.');
      removeRememberedAccount(userId);
      refreshRememberedAccounts();
    } finally {
      setContinuingUserId(null);
    }
  };

  const handleRemoveRememberedAccount = (userId) => {
    removeRememberedAccount(userId);
    refreshRememberedAccounts();
    const removed = rememberedAccounts.find((a) => a.userId === userId);
    if (
      removed?.user?.email &&
      formData.email.trim().toLowerCase() === removed.user.email.toLowerCase()
    ) {
      setFormData((prev) => ({ ...prev, rememberMe: false }));
    }
    toast.success('Account removed from this device');
  };

  const validateForm = () => {
    const errors = {};

    const emailValidationMessage = getEmailValidationError(formData.email, {
      requiredMessage: 'Please enter a User ID',
    });
    validateEmailForSubmit(formData.email);
    if (emailValidationMessage) {
      errors.email = emailValidationMessage;
    }

    if (!formData.password.trim()) {
      errors.password = 'Please provide a password';
    } else if (formData.password.length < 5) {
      errors.password = 'Your password must be at least 5 characters long';
    }

    if (!captchaToken) {
      errors.captcha = 'Please complete the CAPTCHA verification';
    }

    return errors;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    e.stopPropagation();

    const validationErrors = validateForm();
    if (Object.keys(validationErrors).length > 0) {
      setErrors(validationErrors);
      return;
    }
    
    // Clear errors if validation passes
    setErrors({});

    try {
      dispatch(setLoading(true));
      setPendingSuspiciousLogin(false);

      // Dispatch login action - backend determines user type and redirect URL
      const result = await dispatch(loginUser({
        email: formData.email,
        password: formData.password,
        rememberMe: formData.rememberMe,
        captchaToken: captchaToken,
      })).unwrap();

      refreshRememberedAccounts();

      if (isSuspiciousRole(result.user) && result.suspicious_activity) {
        dispatch(setSuspiciousActivity(result.suspicious_activity));
        setPendingSuspiciousLogin(true);
      } else {
        setPendingSuspiciousLogin(false);
      }

      // Reset CAPTCHA after successful login
      setCaptchaToken(null);
      setCaptchaKey(Date.now());

      // Check if 2FA is required
      if (result.requires2FA) {
        // 2FA is required, don't fetch profile yet
        // The SecurityQuestionModal will be shown by useEffect
        return;
      }

      // Show success toast
      toast.success(`Welcome back, ${result.user.first_name}!`);

      // Fetch user profile immediately after login to load profile picture and complete user data
      await dispatch(getUserProfile()).unwrap();

      // Redirection will be handled by useEffect when redirectUrl is set

    } catch (error) {
      // Reset CAPTCHA on error - force regeneration with new key
      setCaptchaToken(null);
      setCaptchaKey(Date.now());

      // Check if account is self-deactivated (shows reactivation button)
      // vs admin-disabled (shows support message only)
      const accountStatus = error?.account_status;
      if (accountStatus === 'self_deactivated') {
        console.log('🔒 Self-deactivated account - showing reactivation modal');
        setShowInactiveModal(true);
      } else if (accountStatus === 'admin_disabled') {
        toast.error(error?.message || 'Your account has been disabled. Please contact support.');
      } else {
        toast.error(error?.message || error || 'Login failed. Please check your credentials and try again.');
      }

    } finally {
      dispatch(setLoading(false));
    }
  };

  const handlePasswordChangeSuccess = () => {
    setShowForcePasswordChange(false);
    dispatch(clearPasswordChangeFlag());
    toast.success('Password changed successfully! Redirecting...');
    
    // Fetch updated profile and continue with normal flow
    dispatch(getUserProfile()).unwrap().then(() => {
      // Trigger normal redirect flow
      if (user) {
        const targetUrl = user.user_type === 'Admin' || user.user_type === 'SubAdmin' 
          ? '/admin' 
          : user.user_type === 'Airliner' 
            ? '/airliner/profile' 
            : '/jetsetter/profile/user-details';
        
        setTimeout(() => {
          navigate(targetUrl);
          onClose();
        }, 500);
      }
    });
  };

  return (
    <div className="max-w-md mx-auto p-6">
      <ForcePasswordChangeModal
        isOpen={showForcePasswordChange}
        onClose={() => {}} // Prevent closing - user must change password
        onSuccess={handlePasswordChangeSuccess}
      />
      <AccountInactiveModal
        isOpen={showInactiveModal}
        onClose={() => setShowInactiveModal(false)}
        email={formData.email}
        onCloseLoginModal={onClose}
      />
      {showInactiveModal || showForcePasswordChange ? null : (
        <>
      <h2 className="text-2xl font-bold text-jet-gray-900 mb-2 text-center">Login</h2>
      <p className="text-jet-gray-600 text-sm mb-6 text-center leading-relaxed">
        Sign in to access your personalized profile and your reservations.
      </p>

      <RememberedAccountsList
        accounts={rememberedAccounts}
        onContinue={handleContinueAsAccount}
        onRemove={handleRemoveRememberedAccount}
        continuingUserId={continuingUserId}
      />

      <form onSubmit={handleSubmit} className="space-y-4">
        <div className="form-group">
          <input
            type="email"
            name="email"
            placeholder="EMAIL OR USER ID"
            value={formData.email}
            onChange={handleChange}
            onBlur={(e) => onEmailBlur(e.target.value)}
            style={{ textTransform: 'uppercase' }}
            className={getEmailValidationInputClass(
              'w-full px-4 py-3 border rounded-md text-sm bg-white text-jet-gray-800 placeholder:text-jet-gray-400 transition-colors duration-300 focus:outline-none focus:shadow-[0_0_0_2px_rgba(255,215,0,0.2)]',
              {
                touched: emailTouched,
                error: emailError,
                value: formData.email,
                defaultBorderClass: 'border-jet-gray-300 focus:border-jet-yellow',
              }
            )}
          />
          <EmailFieldFeedback
            touched={emailTouched}
            error={emailError || errors.email}
            value={formData.email}
          />
        </div>

        <div className="form-group relative">
          <input
            type={showPassword ? 'text' : 'password'}
            name="password"
            placeholder="Password"
            value={formData.password}
            onChange={handleChange}
            className="password-field w-full px-4 py-3 pr-12 border border-jet-gray-300 rounded-md text-sm bg-white text-jet-gray-800 placeholder:text-jet-gray-400 transition-colors duration-300 focus:outline-none focus:border-jet-yellow focus:shadow-[0_0_0_2px_rgba(255,215,0,0.2)]"
          />

          <button
            type="button"
            onClick={() => setShowPassword(!showPassword)}
            className="absolute right-3 top-1/2 -translate-y-1/2 text-jet-gray-500 hover:text-jet-gray-700 transition-colors"
          >
            {showPassword ? <EyeOff size={18} /> : <Eye size={18} />}
          </button>
          {errors.password && <p className="text-red-500 text-sm mt-1">{errors.password}</p>}
        </div>

        <div className="flex items-center py-2">
          <label className="flex items-center cursor-pointer">
            <input
              type="checkbox"
              name="rememberMe"
              checked={formData.rememberMe}
              onChange={handleChange}
              className="w-4 h-4 text-jet-yellow border-jet-gray-300 rounded focus:ring-jet-yellow focus:ring-2 cursor-pointer"
            />
            <span className="ml-2 text-sm text-jet-gray-600">Remember me</span>
          </label>
        </div>

        {/* Text CAPTCHA */}
        <div className="form-group">
          <TextCaptcha
            key={captchaKey}
            onChange={(token) => {
              setCaptchaToken(token);
              if (errors.captcha) {
                setErrors(prev => ({ ...prev, captcha: '' }));
              }
            }}
            onExpired={() => setCaptchaToken(null)}
          />
          {errors.captcha && <p className="text-red-500 text-sm mt-1">{errors.captcha}</p>}
        </div>

        <div className="flex justify-between items-center py-2">
          <button type="button" onClick={onSignUpClick} className="text-jet-gray-600 text-sm hover:text-jet-gray-800 transition-colors duration-200 bg-none border-none cursor-pointer p-0">
            Sign Up
          </button>
          <button type="button" onClick={onForgotPasswordClick} className="text-jet-gray-600 text-sm hover:text-jet-gray-800 transition-colors duration-200 bg-none border-none cursor-pointer p-0">
            Forgot Password ?
          </button>
        </div>

        <button
          type="submit"
          disabled={loading}
          className="w-full bg-jet-yellow text-jet-gray-900 border-0 py-3 px-6 rounded-md text-sm font-semibold cursor-pointer transition-all duration-300 hover:bg-jet-yellow-hover hover:-translate-y-0.5 focus:outline-none focus:shadow-[0_0_0_2px_rgba(255,215,0,0.4)] mt-6 disabled:opacity-50 disabled:cursor-not-allowed"
        >
          {loading ? 'LOGGING IN...' : 'LOGIN'}
        </button>
      </form>
        </>
      )}
    </div>
  );
};

export default LoginForm;